import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import '@/styles/schedule.css';

/**
 * Внеурочные занятия учителя — недельная сетка.
 *
 * Что показываем:
 *   • слоты, привязанные к этому учителю (extracurricular_slots.teacher_id = me);
 *   • если слот пересекается с конкретной датой недели (date_start..date_end),
 *     в ячейку (день × время) рисуем карточку: предмет, время, отметка
 *     «проведено» и количество добавленных учеников.
 *
 * Клик по карточке → страница сессии (тот же экран, что и в админ-расписании
 * внеурочных) — там учитель заполняет тему, отмечает учеников и проведено.
 *
 * Если за неделю ни одного слота не выпало — показываем пустое состояние
 * «На этой неделе у вас нет внеурочных занятий».
 */
const URL_SCHEMA = { week: '' };

const DAYS = [
  { id: 1, short: 'Пн', full: 'Понедельник' },
  { id: 2, short: 'Вт', full: 'Вторник' },
  { id: 3, short: 'Ср', full: 'Среда' },
  { id: 4, short: 'Чт', full: 'Четверг' },
  { id: 5, short: 'Пт', full: 'Пятница' },
  { id: 6, short: 'Сб', full: 'Суббота' },
  { id: 7, short: 'Вс', full: 'Воскресенье' },
];
const MONTHS_RU_GEN = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function fmtLocalIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isoToday() { return fmtLocalIso(new Date()); }
function startOfWeek(iso) {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay() || 7;
  if (dow > 1) d.setDate(d.getDate() - (dow - 1));
  return fmtLocalIso(d);
}
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return fmtLocalIso(d);
}
function fmtWeekRange(start) {
  if (!start) return '';
  // Подстраховка: если в URL не понедельник — снапим, чтобы шапка
  // не разъехалась с сеткой (бэк тоже снапит на свою сторону).
  const monday = startOfWeek(start);
  const a = new Date(monday + 'T00:00:00');
  const b = new Date(addDays(monday, 6) + 'T00:00:00');
  return `${a.getDate()} ${MONTHS_RU_GEN[a.getMonth()]} — ${b.getDate()} ${MONTHS_RU_GEN[b.getMonth()]}`;
}

export default function TeacherExtracurricularPage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const navigate = useNavigate();

  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(null);

  // Подтягиваем неделю к понедельнику и инициализируем URL, если пусто.
  useEffect(() => {
    if (!params.week) {
      setParams({ week: startOfWeek(isoToday()) });
    } else {
      const fixed = startOfWeek(params.week);
      if (fixed !== params.week) setParams({ week: fixed });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!params.week) return;
    let cancelled = false;
    setLoad(true); setError(null);
    api.get(`/api/teacher/extracurricular?week=${encodeURIComponent(params.week)}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [params.week]);

  // Перед шагом ±7 дней снапим на понедельник — иначе дрейф из URL
  // (если туда попала не-понедельничная дата) тащился бы по кнопкам.
  const goPrev  = () => setParams({ week: addDays(startOfWeek(params.week || isoToday()), -7) });
  const goNext  = () => setParams({ week: addDays(startOfWeek(params.week || isoToday()),  7) });
  const goToday = () => setParams({ week: startOfWeek(isoToday()) });

  const slots    = data?.slots    || [];
  const sessions = data?.sessions || [];
  const times    = data?.times    || [];
  const weekdays = data?.weekdays || [];
  const holidays = data?.holidays || [];

  // (slotId|date) → session — для быстрого доступа к статусу занятия.
  const sessionByCell = useMemo(() => {
    const m = new Map();
    for (const s of sessions) m.set(s.slotId + '|' + s.date, s);
    return m;
  }, [sessions]);

  // (timeId|dayOfWeek) → массив слотов в этой ячейке (обычно один,
  // но теоретически у учителя может быть две группы в одно время —
  // например, рисование и шахматы; такого нет, но сетка это допускает).
  const slotsByCell = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const k = s.timeId + '|' + s.dayOfWeek;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    return m;
  }, [slots]);

  const holidayByDate = useMemo(() => {
    const m = new Map();
    for (const h of holidays) m.set(h.date, h);
    return m;
  }, [holidays]);

  // Какие колонки реально показывать. По умолчанию — все 7, чтобы не
  // прыгал layout, когда в одну неделю слот в субботу есть, а в другую нет.
  // Если бы хотели сжимать — фильтровали бы DAYS по DAY_USED.
  const visibleDays = DAYS;

  // Клик по карточке → страница сессии (тема, проведено, состав).
  // Cессия в БД создаётся лениво первым PUT — до этого слот+дата в URL
  // достаточно, чтобы открыть пустую форму.
  const onSlotClick = (slot, dateIso) => {
    navigate(`/teacher/extracurricular/session?slot=${slot.id}&date=${dateIso}`);
  };

  return (
    <>
      <Topbar
        breadcrumb={['Внеурочные занятия', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="sch-toolbar">
        <div className="sch-toolbar__filler" />
        <div className="sch-toolbar__nav">
          <button type="button" className="btn btn--sm" onClick={goPrev} title="Прошлая неделя">
            <Icon name="chevron-left" size={12} />
          </button>
          <span className="sch-toolbar__range mono">
            {params.week ? fmtWeekRange(params.week) : ''}
          </span>
          <button type="button" className="btn btn--sm" onClick={goNext} title="Следующая неделя">
            <Icon name="chevron" size={12} />
          </button>
          <button type="button" className="btn btn--sm" onClick={goToday}>Сегодня</button>
        </div>
      </div>

      <div className="sch-content">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {loading && <div className="ref-empty">Загружаем…</div>}
        {!loading && !error && slots.length === 0 && (
          <div className="ref-empty">
            На этой неделе у вас нет внеурочных занятий.
            Используйте кнопки навигации, чтобы найти нужную неделю.
          </div>
        )}
        {!loading && !error && slots.length > 0 && (
          <div className="sch-grid" style={{ '--days': visibleDays.length }}>
            <div className="sch-grid__corner">Время</div>
            {visibleDays.map((d) => {
              const wd = weekdays.find((w) => w.n === d.id);
              const date = wd?.date || '';
              const h = date ? holidayByDate.get(date) : null;
              const headClass = 'sch-grid__dayhead'
                + (wd?.isToday ? ' is-today' : '')
                + (h ? ' is-holiday' : '');
              return (
                <div key={d.id} className={headClass}>
                  <div className="sch-grid__dayhead-name">{d.full}</div>
                  <div className="sch-grid__dayhead-short">
                    {d.short}{date ? ' · ' + date.slice(8) + '.' + date.slice(5, 7) : ''}
                  </div>
                  {h && (
                    <div className="sch-grid__dayhead-holiday" title={h.label || ''}>
                      {h.kind === 'vacation' ? 'Каникулы'
                        : h.kind === 'holiday' ? 'Выходной'
                        : 'Сокр. день'}
                    </div>
                  )}
                </div>
              );
            })}

            {times.map((t) => (
              <React.Fragment key={t.id}>
                <div className="sch-grid__time">
                  <div className="sch-grid__time-from mono">{t.timeStart}</div>
                  <div className="sch-grid__time-to mono">до {t.timeEnd}</div>
                </div>
                {visibleDays.map((d) => {
                  const wd = weekdays.find((w) => w.n === d.id);
                  const cellDate = wd?.date || '';
                  const cellSlots = (slotsByCell.get(t.id + '|' + d.id) || [])
                    // Слот действует именно на этой конкретной дате?
                    // (active_between на бекенде даёт слот, валидный
                    // в любую дату недели; здесь сужаем до этого дня).
                    .filter((s) => cellDate && s.dateStart <= cellDate && s.dateEnd >= cellDate);
                  return (
                    <div key={d.id} className={'sch-cell' + (cellSlots.length === 0 ? ' is-empty' : '')}>
                      {cellSlots.map((s) => (
                        <ExtraSlotCard
                          key={s.id}
                          slot={s}
                          session={sessionByCell.get(s.id + '|' + cellDate)}
                          onOpen={() => onSlotClick(s, cellDate)}
                        />
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Карточка внеурочного занятия в сетке. Цвет левого бордера говорит
 * о статусе: зелёный «насыщенный» — проведено; нейтрально-зелёный — запланировано.
 * В правом верхнем углу — количество добавленных учеников (если уже
 * есть сессия с составом).
 *
 * Клик открывает страницу сессии: тема, отметка «проведено», состав
 * учеников и оценки.
 */
function ExtraSlotCard({ slot, session, onOpen }) {
  const conducted = !!session?.conducted;
  const studentsCount = session?.studentsCount;
  const hasStudents = studentsCount != null;
  const title = conducted
    ? 'Занятие проведено — открыть'
    : 'Открыть занятие за этот день';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className={'sch-lesson tch-lesson is-clickable'
        + (conducted ? ' tch-extra--conducted' : '')}
      style={{ borderLeftColor: conducted ? '#059669' : 'var(--green-500, #10b981)' }}
      title={title}
    >
      <div className="sch-lesson__top">
        <span className="sch-lesson__time mono">{slot.timeStart}–{slot.timeEnd}</span>
        {hasStudents && (
          <span className="tch-extra__count" title={'На занятии: ' + studentsCount}>
            <Icon name="users" size={11} />
            <span>{studentsCount}</span>
          </span>
        )}
      </div>
      <div className="sch-lesson__name">{slot.subjectName || '— без предмета —'}</div>
      {slot.maxStudents > 0 && (
        <div className="sch-lesson__meta">до {slot.maxStudents} чел.</div>
      )}
    </div>
  );
}
